import { Link } from 'react-router-dom';
import { DocSection, Callout, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'red-warning', label: 'The red compute warning in Settings' },
  { id: 'update-driver', label: 'Update your NVIDIA driver & restart' },
  { id: 'confirm-cuda', label: 'Confirm you have a CUDA GPU' },
  { id: 'low-vram', label: 'Low-VRAM slowdowns' },
];

export default function Body() {
  return (
    <>
      <DocSection id="red-warning" title="The red compute warning in Settings">
        <p>
          If <span style={kbd}>Settings → System</span> shows a red compute warning, ClipRadar has
          not found a usable NVIDIA CUDA GPU on this machine. Analysis and Ultra Search run on the
          GPU, and ClipRadar won't start without one — so nothing works until it's found; there is
          no fallback path in a real install.
        </p>
        <DocShot
          slug="gpu-not-detected"
          shot="compute-warning"
          alt="Settings → System showing a red compute-device warning"
          caption="Settings → System reports the compute device ClipRadar actually detected."
        />
        <p>Work through the next two sections in order — they resolve almost every case.</p>
      </DocSection>

      <DocSection id="update-driver" title="Update your NVIDIA driver & restart">
        <p>
          Most "no GPU found" cases are a stale or mismatched NVIDIA driver, not missing hardware.
        </p>
        <ol>
          <li>Update your NVIDIA driver (GeForce Experience, the NVIDIA app, or a fresh download from nvidia.com).</li>
          <li>Restart Windows — not just ClipRadar. A driver install often doesn't take effect until the whole machine reboots.</li>
          <li>Reopen ClipRadar and check <span style={kbd}>Settings → System</span> again.</li>
        </ol>
      </DocSection>

      <DocSection id="confirm-cuda" title="Confirm you have a CUDA GPU">
        <p>
          ClipRadar requires an NVIDIA GPU with CUDA support — there is no CPU mode for real use.
          If the warning is still there after a driver update and a restart, confirm the GPU itself
          qualifies: open Windows Device Manager → Display adapters and check the exact model name.
          An AMD or Intel GPU, or a machine with no discrete GPU at all, cannot run ClipRadar no
          matter how current the drivers are.
        </p>
        <p>
          See <Link to="/docs/reference/system-requirements#gpu">System requirements</Link> for the
          exact GPU and driver minimums.
        </p>
      </DocSection>

      <DocSection id="low-vram" title="Low-VRAM slowdowns">
        <p>
          A GPU that ClipRadar detects fine can still run into a second, different limit: not
          enough free VRAM. ClipRadar automatically shrinks its analysis batch size to fit whatever
          VRAM is actually free — including VRAM other apps, like Premiere Pro, are holding onto —
          but if even the smallest batch doesn't fit, Windows silently spills the work into system
          RAM instead of erroring, which runs roughly 10× slower. ClipRadar shows a spill warning
          when this happens so the slowdown isn't a mystery.
        </p>
        <DocShot
          slug="gpu-not-detected"
          shot="spill-warning"
          alt="The spill-warning banner shown when VRAM is too tight for the current batch"
        />
        <Callout kind="tip">
          Closing Premiere Pro (or any other GPU-heavy app) while ClipRadar analyzes footage frees
          VRAM immediately — no restart needed.
        </Callout>
        <p>
          Ultra Search is the most VRAM-sensitive feature and falls back to plain search on tight
          cards rather than failing outright. For the full picture of how VRAM shapes batch sizing
          and what degrades first, see{' '}
          <Link to="/docs/concepts/models-and-your-gpu#weak-card">AI models & your GPU</Link>.
        </p>
      </DocSection>
    </>
  );
}
